import { Select } from 'antd'
import { useLocality } from 'location/LocalityProvider'
import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { filtersAPI } from '../../../axios/axios'
import { QueryKeyENUM } from '../../atoms/Filters/ApplyFilters.model'
import type { ItemType } from '../ApplyFilters.model'
import { SFilterBlock, STitle } from '../Filters.styled'

type Option = { value: string | number; label: string }
type Key =
  | QueryKeyENUM.teacherId
  | QueryKeyENUM.programType
  | QueryKeyENUM.programId
  | QueryKeyENUM.eduForm

interface Props {
  selectedFilters: Record<QueryKeyENUM, ItemType>
  setFilters: (key: QueryKeyENUM, item: ItemType | null) => void
}

const fields: { key: Key; title: string; search?: boolean }[] = [
  { key: QueryKeyENUM.teacherId, title: 'Педагог', search: true },
  { key: QueryKeyENUM.programType, title: 'Тип программы' },
  { key: QueryKeyENUM.programId, title: 'Программа', search: true },
  { key: QueryKeyENUM.eduForm, title: 'Форма обучения' },
]

const ProgramDetailFilter: FC<Props & { field: (typeof fields)[number] }> = ({
  selectedFilters,
  setFilters,
  field,
}) => {
  const selected = selectedFilters?.[field.key]
  const [search, setSearch] = useState('')
  const [options, setOptions] = useState<Option[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const unitId = selectedFilters?.[QueryKeyENUM.unitID]?.value
  const { locality } = useLocality()
  const localityGUID = locality?.localityGUID || ''
  const needsLabel = String(selected?.name || '').startsWith('№ ')

  useEffect(() => {
    const controller = new AbortController()
    setOptions([])
    setError(false)
    setLoading(false)
    if (field.search && search.trim().length < 2 && !needsLabel)
      return () => controller.abort()
    setLoading(true)
    const timer = setTimeout(
      async () => {
        try {
          const result = await filtersAPI.getProgramFilterOptions(
            field.key,
            search.trim(),
            {
              signal: controller.signal,
              params: {
                unit_id: unitId,
                localityGUID,
                selected_id: selected?.value,
              },
            },
          )
          if (!controller.signal.aborted) setOptions(result)
        } catch (requestError) {
          if (!controller.signal.aborted) setError(true)
        } finally {
          if (!controller.signal.aborted) setLoading(false)
        }
      },
      field.search ? 300 : 0,
    )
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [
    field.key,
    field.search,
    search,
    unitId,
    localityGUID,
    selected?.value,
    needsLabel,
  ])

  useEffect(() => {
    const option = options.find(
      (item) => String(item.value) === String(selected?.value),
    )
    if (option && option.label !== selected?.name) {
      setFilters(field.key, { value: option.value, name: option.label })
    }
  }, [options, selected?.value, selected?.name, setFilters, field.key])

  return (
    <SFilterBlock>
      <STitle>{field.title}</STitle>
      <Select
        aria-label={field.title}
        allowClear
        showSearch
        labelInValue
        value={
          selected?.value
            ? {
                value: selected.value,
                label:
                  options.find(
                    (option) => String(option.value) === String(selected.value),
                  )?.label ||
                  selected.name ||
                  `№ ${selected.value}`,
              }
            : undefined
        }
        placeholder={field.search ? 'Введите не менее двух символов' : 'Все'}
        filterOption={field.search ? false : undefined}
        optionFilterProp="label"
        onSearch={setSearch}
        loading={loading}
        notFoundContent={
          error
            ? 'Не удалось загрузить варианты. Повторите поиск.'
            : 'Нет вариантов'
        }
        options={options}
        onChange={(option) =>
          setFilters(
            field.key,
            option
              ? {
                  value: option.value,
                  name: String(option.label),
                }
              : null,
          )
        }
      />
    </SFilterBlock>
  )
}

export const ProgramDetailsFilters: FC<Props> = (props) => (
  <>
    {fields.map((field) => (
      <ProgramDetailFilter key={field.key} field={field} {...props} />
    ))}
  </>
)
